import React, { useEffect, useState } from 'react';
import {
    DefaultButton,
    FontWeights,
    getTheme,
    IButtonStyles,
    IconButton,
    mergeStyleSets,
    Modal,
    Panel,
    PanelType,
    TextField,
} from '@fluentui/react';
import TableDefaultComponent from '../table-default/table-default.component';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemCliente from '../../models/ItemCliente';
import ItemDireccion from '../../models/ItemDireccion';

const theme = getTheme();

export interface ICustomerCardContactFields {
    Nombre: string;
    Direccion: string;
    CPostal: string;
    Poblacion: string;
    Provincia: string;
    Pais: string;
    Telefono: string;
    Email: string;
    Web: string;
    PosicionGPS: string;
}

interface ICustomerCardProps {
    disabled?: boolean;
    hideSearch?: boolean;
    /** Optional label shown above the field, like FluentUI TextField label */
    label?: string;
    nombre: string;
    direccion: string;
    cpostal: string;
    poblacion: string;
    provincia: string;
    pais: string;
    telefono: string;
    email: string;
    web: string;
    posicionGPS?: string;
    /** Optional override text shown in the input field */
    displayText?: string;
    /** If provided, the search button opens a list of these addresses instead of searching clients */
    addressList?: ItemDireccion[];
    onCustomerSelected: (customer: ItemCliente) => void;
    onContactChanged: (fields: ICustomerCardContactFields) => void;
}

const classes = mergeStyleSets({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
    },
    inputField: {
        flex: 1,
        border: '1px solid ' + theme.palette.neutralTertiaryAlt,
        borderRadius: '2px',
        padding: '0 8px',
        height: '32px',
        backgroundColor: theme.palette.white,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
    },
    inputText: {
        flex: 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: '14px',
    },
    inputPlaceholder: {
        flex: 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: '14px',
        color: theme.palette.neutralTertiary,
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: '2px',
        flexShrink: 0,
    },
    modalContainer: {
        display: 'flex',
        flexFlow: 'column nowrap',
        alignItems: 'stretch',
        width: '80vw',
    },
    modalHeader: [
        theme.fonts.xLargePlus,
        {
            flex: '1 1 auto',
            color: theme.palette.neutralPrimary,
            display: 'inline-flex',
            alignItems: 'center',
            fontWeight: FontWeights.semibold,
            padding: '12px 12px 14px 24px',
        },
    ],
    modalBody: {
        flex: '4 4 auto',
        padding: '0 24px 24px 24px',
        overflowY: 'auto',
    },
    panelField: {
        marginBottom: '12px',
    },
    panelFieldRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '12px',
    },
    panelFieldCP: {
        flex: '0 0 100px',
    },
    panelFieldHalf: {
        flex: 1,
    },
    addressRow: {
        cursor: 'pointer',
        padding: '10px 12px',
        borderBottom: '1px solid ' + theme.palette.neutralLight,
        selectors: {
            ':hover': {
                backgroundColor: theme.palette.neutralLighter,
            },
        },
    },
    addressRowName: {
        fontWeight: FontWeights.semibold,
        display: 'block',
    },
    addressRowSub: {
        fontSize: '12px',
        color: theme.palette.neutralSecondary,
        display: 'block',
    },
    addressEmpty: {
        padding: '16px',
        color: theme.palette.neutralSecondary,
        fontStyle: 'italic',
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '14px',
        display: 'block',
        padding: '5px 0 4px',
    },
});

const primaryButtonStyles: IButtonStyles = {
    root: {
        backgroundColor: 'white',
        color: 'var(--color-primario)',
        border: '1px solid var(--color-primario)',
    },
    rootHovered: {
        backgroundColor: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
        color: '#ffffff',
    },
};

const iconButtonStyles: IButtonStyles = {
    root: {
        backgroundColor: 'white',
        color: 'var(--color-primario)',
        border: '1px solid var(--color-primario)',
        height: '32px',
        width: '32px',
        selectors: {
            span: { selectors: { i: { color: 'var(--color-primario)' } } },
        },
    },
    rootHovered: {
        backgroundColor: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
        selectors: {
            span: { selectors: { i: { color: '#ffffff' } } },
        },
    },
};

const CustomerCardComponent = (props: ICustomerCardProps): JSX.Element => {
    const auth = useAuthStatus();

    // Search modal state
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
    const [customers, setCustomers] = useState<ItemCliente[]>([]);
    const [filteredCustomers, setFilteredCustomers] = useState<ItemCliente[]>([]);
    const [filteredAddresses, setFilteredAddresses] = useState<ItemDireccion[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchError, setSearchError] = useState<string>('');

    // Edit panel state
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
    const [editFields, setEditFields] = useState<ICustomerCardContactFields>({
        Nombre: props.nombre,
        Direccion: props.direccion,
        CPostal: props.cpostal,
        Poblacion: props.poblacion,
        Provincia: props.provincia,
        Pais: props.pais,
        Telefono: props.telefono,
        Email: props.email,
        Web: props.web,
        PosicionGPS: props.posicionGPS || '',
    });

    // Sync edit fields when parent props change (e.g. after selecting a new customer)
    useEffect(() => {
        setEditFields({
            Nombre: props.nombre,
            Direccion: props.direccion,
            CPostal: props.cpostal,
            Poblacion: props.poblacion,
            Provincia: props.provincia,
            Pais: props.pais,
            Telefono: props.telefono,
            Email: props.email,
            Web: props.web,
            PosicionGPS: props.posicionGPS || '',
        });
    }, [props.nombre, props.direccion, props.cpostal, props.poblacion, props.provincia, props.pais, props.telefono, props.email, props.web, props.posicionGPS]);

    const onSearchClick = (): void => {
        setSearchText('');
        setSearchError('');
        if (props.addressList !== undefined) {
            // Address mode: show pre-loaded address list
            setFilteredAddresses(props.addressList);
            setIsSearchOpen(true);
        } else if (auth.user) {
            // Client mode: call API
            setSearchLoading(true);
            setIsSearchOpen(true);
            ApiService.Post<ItemCliente[]>('OrdenesTrabajo', 'GetListaClientes', auth.user).then(
                (response: ItemCliente[]) => {
                    setCustomers(response);
                    setFilteredCustomers(response);
                    setSearchLoading(false);
                },
                (error: string) => {
                    setSearchError(error);
                    setSearchLoading(false);
                }
            );
        }
    };

    const onFilterTextChange = (
        _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ): void => {
        setSearchText(newValue ?? '');
        if (props.addressList !== undefined) {
            setFilteredAddresses(
                newValue
                    ? props.addressList.filter(
                        (a: ItemDireccion) =>
                            (a.Nombre + ' ' + a.Direccion + ' ' + a.Poblacion)
                                .toLowerCase()
                                .indexOf(newValue.toLowerCase()) > -1
                      )
                    : props.addressList
            );
        } else {
            setFilteredCustomers(
                newValue
                    ? customers.filter((i: ItemCliente) => i.Nombre.toLowerCase().indexOf(newValue.toLowerCase()) > -1)
                    : customers
            );
        }
    };

    const onAddressSelected = (address: ItemDireccion): void => {
        setIsSearchOpen(false);
        const fields: ICustomerCardContactFields = {
            Nombre: address.Nombre,
            Direccion: address.Direccion,
            CPostal: address.CPostal,
            Poblacion: address.Poblacion,
            Provincia: address.Provincia,
            Pais: address.Pais,
            Telefono: '',
            Email: '',
            Web: '',
            PosicionGPS: '',
        };
        setEditFields(fields);
        props.onContactChanged(fields);
    };

    const onItemSelected = (customer: ItemCliente): void => {
        setCustomers([]);
        setIsSearchOpen(false);
        props.onCustomerSelected(customer);
    };

    const onEditFieldChange = (field: keyof ICustomerCardContactFields) => (
        _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ): void => {
        const updated: ICustomerCardContactFields = { ...editFields, [field]: newValue ?? '' };
        setEditFields(updated);
        props.onContactChanged(updated);
    };

    const displayValue = props.displayText !== undefined
        ? props.displayText
        : props.nombre
            ? [props.nombre, props.direccion].filter(Boolean).join(': ')
            : '';

    return (
        <div>
            {props.label && (
                <label className={classes.fieldLabel}>
                    {props.label}
                </label>
            )}
            <div className={classes.wrapper}>
            {/* Contact as inline text-field (matching desktop ButtonEdit) */}
            <div className={classes.inputField}>
                {displayValue ? (
                    <span className={classes.inputText}>{displayValue}</span>
                ) : (
                    <span className={classes.inputPlaceholder}>(sin cliente seleccionado)</span>
                )}
            </div>

            {/* Action buttons */}
            {!props.disabled && (
                <div className={classes.actions}>
                    {!props.hideSearch && (
                        <IconButton
                            iconProps={{ iconName: 'Search' }}
                            styles={iconButtonStyles}
                            title='Cambiar cliente'
                            onClick={onSearchClick}
                        />
                    )}
                    <IconButton
                        iconProps={{ iconName: 'Edit' }}
                        styles={iconButtonStyles}
                        title='Editar contacto'
                        onClick={() => setIsPanelOpen(true)}
                    />
                </div>
            )}

            {/* Search modal */}
            {isSearchOpen && (
                <Modal
                    titleAriaId='customer-card-search-modal'
                    isOpen={true}
                    onDismiss={() => setIsSearchOpen(false)}
                    isBlocking={false}
                    containerClassName={classes.modalContainer}>
                    <div className={classes.modalHeader}>
                        <IconButton
                            styles={iconButtonStyles}
                            iconProps={{ iconName: 'Back' }}
                            onClick={() => setIsSearchOpen(false)}
                        />
                        <span className='mx-1'>
                            {props.addressList !== undefined ? 'Direcciones' : 'Clientes'}
                        </span>
                    </div>
                    <div className={classes.modalBody}>
                        <TextField
                            label='Filtrar:'
                            value={searchText}
                            onChange={onFilterTextChange}
                        />
                        {props.addressList !== undefined ? (
                            filteredAddresses.length === 0 ? (
                                <div className={classes.addressEmpty}>No hay direcciones disponibles.</div>
                            ) : (
                                filteredAddresses.map((a: ItemDireccion, i: number) => (
                                    <div
                                        key={i}
                                        className={classes.addressRow}
                                        onClick={() => onAddressSelected(a)}>
                                        <span className={classes.addressRowName}>{a.Nombre}</span>
                                        <span className={classes.addressRowSub}>
                                            {[a.Direccion, a.CPostal, a.Poblacion, a.Provincia]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </span>
                                    </div>
                                ))
                            )
                        ) : (
                            <TableDefaultComponent
                                items={filteredCustomers}
                                onRowClick={onItemSelected}
                            />
                        )}
                    </div>
                    <ErrorMessageComponent message={searchError} />
                    <LoaderComponent loading={searchLoading} />
                </Modal>
            )}

            {/* Contact edit panel */}
            <Panel
                isOpen={isPanelOpen}
                onDismiss={() => setIsPanelOpen(false)}
                headerText='Editar'
                type={PanelType.medium}
                isFooterAtBottom={true}
                layerProps={{ eventBubblingEnabled: true }}
                onRenderFooterContent={() => (
                    <DefaultButton
                        text='Guardar'
                        styles={primaryButtonStyles}
                        onClick={() => setIsPanelOpen(false)}
                    />
                )}>
                <div className={classes.panelField}>
                    <TextField
                        label='Nombre'
                        maxLength={100}
                        value={editFields.Nombre}
                        onChange={onEditFieldChange('Nombre')}
                    />
                </div>
                <div className={classes.panelField}>
                    <TextField
                        label='Dirección'
                        maxLength={100}
                        value={editFields.Direccion}
                        onChange={onEditFieldChange('Direccion')}
                    />
                </div>
                <div className={classes.panelFieldRow}>
                    <div className={classes.panelFieldCP}>
                        <TextField
                            label='C.P.'
                            maxLength={50}
                            value={editFields.CPostal}
                            onChange={onEditFieldChange('CPostal')}
                        />
                    </div>
                    <div className={classes.panelFieldHalf}>
                        <TextField
                            label='Población'
                            maxLength={100}
                            value={editFields.Poblacion}
                            onChange={onEditFieldChange('Poblacion')}
                        />
                    </div>
                </div>
                <div className={classes.panelField}>
                    <TextField
                        label='Provincia'
                        maxLength={100}
                        value={editFields.Provincia}
                        onChange={onEditFieldChange('Provincia')}
                    />
                </div>
                <div className={classes.panelField}>
                    <TextField
                        label='País'
                        maxLength={50}
                        value={editFields.Pais}
                        onChange={onEditFieldChange('Pais')}
                    />
                </div>
                <div className={classes.panelFieldRow}>
                    <div className={classes.panelFieldHalf}>
                        <TextField
                            label='Teléfono'
                            maxLength={50}
                            value={editFields.Telefono}
                            onChange={onEditFieldChange('Telefono')}
                        />
                    </div>
                    <div className={classes.panelFieldHalf}>
                        <TextField
                            label='Email'
                            maxLength={100}
                            value={editFields.Email}
                            onChange={onEditFieldChange('Email')}
                        />
                    </div>
                </div>
                <div className={classes.panelField}>
                    <TextField
                        label='Web'
                        maxLength={200}
                        value={editFields.Web}
                        onChange={onEditFieldChange('Web')}
                    />
                </div>
                <div className={classes.panelField}>
                    <TextField
                        label='Coordenadas GPS'
                        maxLength={100}
                        value={editFields.PosicionGPS}
                        onChange={onEditFieldChange('PosicionGPS')}
                        placeholder='latitud,longitud'
                    />
                </div>
            </Panel>
            </div>
        </div>
    );
};

export default CustomerCardComponent;
